/**
 * Sitting a practice test, as pure functions over a plain object.
 *
 * Kept apart from ui.js for the same reason study.js is: the interesting
 * rules here — what skip does to the score, what "I don't know" does
 * differently, how a select-all is marked — are worth testing without a DOM,
 * and they have to agree exactly with `alter/quiz.py`, which marks the same
 * attempt again on the desktop side.
 *
 * **The two sides must not drift.** The browser marks so a score appears the
 * moment you submit; Alter marks again when the answers reach it, because
 * only Alter can read the written ones. If they disagreed about what skip
 * means, the score would change under you after the fact — so the rules are
 * written twice, deliberately, with the same tests behind each.
 *
 * Every function returns a new attempt rather than mutating the one it was
 * given, so a caller can never half-apply a move.
 *
 * An attempt holds:
 *   at        which question is showing
 *   answers   { questionId: value | 'skipped' | 'unknown' }
 *   submitted whether it has been marked
 */

export const SKIPPED = 'skipped';
export const UNKNOWN = 'unknown';

export const MULTIPLE = 'multiple_choice';
export const SELECT_ALL = 'select_all';
export const WRITTEN = 'free_response';

/**
 * `id` names this sitting, and it is made here rather than at the moment of
 * submitting so that a failed send retried later carries the SAME id. The
 * server stores an attempt once per id, which is what stops a retry from
 * counting every missed topic twice and weighting the next test toward
 * something he only got wrong once. Retaking a test makes a new attempt and
 * so a new id, which is a second sitting and should count twice.
 *
 * Passed in rather than generated here so this module stays a pure function
 * of its arguments and its tests do not have to stub a random source.
 */
export function createAttempt(questionCount, id = '') {
  return {
    id,
    count: Math.max(0, questionCount),
    at: 0,
    answers: {},
    submitted: false,
  };
}

export function goTo(attempt, index) {
  if (attempt.count === 0) return attempt;
  const at = Math.min(Math.max(0, index), attempt.count - 1);
  return { ...attempt, at };
}

export const next = (attempt) => goTo(attempt, attempt.at + 1);
export const previous = (attempt) => goTo(attempt, attempt.at - 1);

/**
 * Answer the question with `id`. A submitted attempt ignores everything —
 * a score you can still change is not a score.
 */
export function answer(attempt, id, value) {
  if (attempt.submitted) return attempt;
  return { ...attempt, answers: { ...attempt.answers, [id]: value } };
}

/**
 * Tick or untick one option of a select-all.
 *
 * Toggling rather than setting, because that is what a checkbox does — and
 * un-ticking the last box leaves an empty array, which is a real answer
 * ("none of these") and marked wrong, rather than an unanswered question.
 */
export function toggle(attempt, id, option) {
  if (attempt.submitted) return attempt;
  const had = attempt.answers[id];
  const picked = Array.isArray(had) ? had.slice() : [];
  const where = picked.indexOf(option);
  if (where === -1) picked.push(option);
  else picked.splice(where, 1);
  picked.sort((a, b) => a - b);
  return answer(attempt, id, picked);
}

export const skip = (attempt, id) => answer(attempt, id, SKIPPED);
export const dontKnow = (attempt, id) => answer(attempt, id, UNKNOWN);

/** Undo a skip or a don't-know, so a mis-tap is not permanent. */
export function clear(attempt, id) {
  if (attempt.submitted) return attempt;
  const answers = { ...attempt.answers };
  delete answers[id];
  return { ...attempt, answers };
}

/** How many questions have been dealt with, one way or another. */
export function answered(attempt) {
  return Object.keys(attempt.answers).length;
}

/**
 * Whether one question has been given an answer proper — not skipped, not
 * "I don't know", not blank. What the progress dots colour by.
 */
export function isAnswered(attempt, id) {
  const given = attempt.answers[id];
  if (given === undefined || given === SKIPPED || given === UNKNOWN) return false;
  if (Array.isArray(given)) return given.length > 0;
  return String(given).trim() !== '' || given === 0;
}

/* ── marking ──────────────────────────────────────────────────
 *
 * The same rules as `alter/quiz.py`, and the reason they are the same is in
 * the module docstring. Read that before changing either.
 */

function sameSet(a, b) {
  if (a.length !== b.length) return false;
  const left = a.slice().sort((x, y) => x - y);
  const right = b.slice().sort((x, y) => x - y);
  return left.every((value, index) => value === right[index]);
}

/**
 * Mark one question against what was given.
 *
 * Returns 'right' | 'wrong' | 'unmarked'. Written answers are always
 * 'unmarked' here: nothing in a browser can read prose, and guessing at it
 * would be a score that says you understood something you did not.
 */
export function markOne(question, given) {
  if (given === SKIPPED) return 'skipped';
  // UNKNOWN before WRITTEN, and the order is the whole point. Allen: "I dont
  // know will just mark it down as wrong." Checking the KIND first sent a
  // free-response "I don't know" back as `unmarked`, where it sat forever --
  // Alter's marker skips an UNKNOWN too, correctly, since there is nothing
  // there to read -- so it was never scored and its topic was never recorded
  // as missed. `alter/quiz.py` has always had this order; this file did not,
  // which is exactly the drift the two copies are meant not to have.
  if (given === UNKNOWN || given === undefined) return 'wrong';
  if (question.kind === WRITTEN) return 'unmarked';

  const picked = Array.isArray(given) ? given : [given];
  const numbers = picked
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value));
  return sameSet(numbers, question.answer || []) ? 'right' : 'wrong';
}

/**
 * Mark the whole attempt.
 *
 * `outOf` counts everything that was NOT skipped — Allen's rule: "skip means
 * that I think its not relevant and wont be graded/impact the grade". So a
 * skipped question leaves the denominator as well as the numerator; scoring
 * out of the full set would make skipping a penalty.
 */
export function mark(questions, attempt) {
  const marked = (questions || []).map((question) => ({
    id: question.id,
    topic: question.topic || '',
    kind: question.kind,
    outcome: markOne(question, attempt.answers[question.id]),
    given: attempt.answers[question.id],
  }));

  const right = marked.filter((m) => m.outcome === 'right').length;
  const wrong = marked.filter((m) => m.outcome === 'wrong').length;
  const unmarked = marked.filter((m) => m.outcome === 'unmarked').length;
  const skipped = marked.filter((m) => m.outcome === 'skipped').length;

  return {
    marked,
    right,
    wrong,
    unmarked,
    skipped,
    outOf: marked.length - skipped,
    // The topics that went wrong, without repeats. Skipped ones are not
    // here: he said they were not relevant, and weighting the next test
    // toward them would be arguing with him.
    missedTopics: marked
      .filter((m) => m.outcome === 'wrong' && m.topic)
      .map((m) => m.topic)
      .filter((topic, index, all) => all.indexOf(topic) === index),
  };
}

export function submit(questions, attempt) {
  if (attempt.submitted) return attempt;
  return { ...attempt, submitted: true, result: mark(questions, attempt) };
}

/**
 * The score in a sentence, honest about what is not marked yet.
 *
 * Mirrors `quiz.say_score`. The written answers are counted separately
 * rather than folded in, because a number that quietly excludes five
 * questions is a number nobody can act on.
 */
export function sayScore(result) {
  if (!result || result.outOf === 0) return 'Nothing to mark — every question was skipped.';
  let said = `${result.right} out of ${result.outOf}`;
  if (result.unmarked) {
    said += `, with ${result.unmarked} written ${result.unmarked === 1 ? 'answer' : 'answers'} for Alter to mark`;
  }
  if (result.skipped) {
    said += ` (${result.skipped} skipped, not counted)`;
  }
  return `${said}.`;
}

/** What gets sent back to Alter. Answers, not the score — Alter re-marks. */
/**
 * What to say when a finished sitting could not be sent.
 *
 * The reason goes in. It used to be thrown away — `.catch(() => …)` — and the
 * page said only that it had failed, so when Allen's nineteen-question test
 * would not send there was nothing anywhere saying whether it was a 400, an
 * expired sign-in or no network. The attempt was gone and so was the evidence.
 *
 * It also used to send him to the class page to try again. There is nothing
 * there to press: re-opening a test starts a NEW sitting with a new attempt
 * id, which the server correctly counts as a second sitting. The retry has to
 * be here, on the same attempt, which is exactly what the server's
 * idempotency on `attemptId` is for.
 */
export function sendFailure(error) {
  const why = String(error?.message || '').trim();
  return 'Could not send it to Alter'
    + (why ? ` — ${why}` : '')
    + '. Until it goes, Alter will not know what you got wrong. Try again.';
}

export function toSubmission(test, attempt) {
  return {
    attemptId: attempt.id || '',
    testId: test.id,
    course: test.course || '',
    answers: { ...attempt.answers },
  };
}
